// @ts-check
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useRef, useState
} from 'react';
import styled, { withTheme } from 'styled-components';
import { Slide } from '@codex/animations';
import { get, useToggle } from '@codex/helpers';
import { Button, Link } from '@codex/basics';
import { Popup } from '@codex/organisms';
import { HeaderLogo } from './HeaderLogo';
import HeaderSearchProduct from './HeaderSearchProduct';
import HeaderMenu from './HeaderMenu';
import { LinkItem, HeaderMenuSpacer } from './HeaderMenu/LinkItem';
import DesktopHeaderNavigation from './HeaderNavigation/DesktopNavigation';
import MobileHeaderNavigation from './HeaderNavigation/MobileNavigation';
import AccountMenu from './AccountMenu';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.default.fontFamily};
  position: relative;
  max-width: 1240px;
  margin: 0 auto;
  padding: ${({ theme }) => (theme.name === 'brightworld' ? '5px 10px' : '5px 10px 0')};

  ${({ theme }) => (theme.name === 'brightworld' ? 'background-color:' : 'background-image:') +
    theme.colors.header.background};

  ${HeaderMenu} {
    z-index: 99;
  }

  ${Slide} {
    box-sizing: border-box;
    position: absolute;
    top: ${({ theme }) => (theme.name === 'brightworld' ? '97px' : '95px')};
    box-shadow: 0 3px 3px rgba(102, 102, 102, 0.2);
    padding: 10px;
    background-color: white;
    left: 0;
    right: 0;
    z-index: 2;

    @media ${({ theme }) => theme.breakpoints.desktop} {
      display: none;
    }

    @media ${({ theme }) => theme.breakpoints.tablet} {
      top: ${({ theme }) => (theme.name === 'brightworld' ? '107px' : '92px')};
    }
  }

  @media ${({ theme }) => theme.breakpoints.mobileMin} {
    padding: 5px 15px 0;
  }

  @media ${({ theme }) => theme.breakpoints.desktop} {
    justify-content: space-between;
    padding: 10px 20px 0;
    background-image: none;
    background: none;
  }
`;

const HeaderLogoWrapper = styled.div`
  padding-left: 10px;
`;

const DesktopHeaderSearchProduct = styled(HeaderSearchProduct)`
  @media ${({ theme }) => `${theme.breakpoints.touch}`} {
    display: none;
  }
`;

const PopupContentWrapper = styled.div`
  max-width: 320px;
  padding: 20px;
`;

const PopupActionWrapper = styled.div`
  text-align: center;
  padding: 20px 25px 0;

  ${Link} {
    margin-top: 10px;
    display: block;
  }
`;

DesktopHeaderSearchProduct.displayName = 'DesktopHeaderSearchProduct';

const TouchHeaderLinkItem = styled(LinkItem)`
  @media ${({ theme }) => theme.breakpoints.desktop} {
    display: none;
  }
`;

const Header = (props) => {
  const refHeaderWrapper = useRef(null);
  const [navigation, setNavigation] = useState(props.initialNavigation);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [mobileNavigationVisible, setMobileNavigationVisible] = useState(false);
  const [openSearch, toggleSearch, searchRef] = useToggle(false, {
    clickOutside: true
  });
  const [openMobileRedirectPopup, toggleMobileRedirectPopup] = useToggle();
  const [targetMobileSiteURl, setTargetMobileSiteUrl] = useState(undefined);
  const [fmoRedirectPopupContent, setFmoRedirectPopupContent] = useState(
    undefined
  );

  const handleFmoRedirectModalIntent = (e) => {
    if (e.detail.targetUrl.includes(props.mobileSiteBaseUrl)) {
      e.preventDefault();
      setTargetMobileSiteUrl(e.detail.targetUrl);
      toggleMobileRedirectPopup(true);
    } else {
      e.detail.ifNotOpening();
    }
  };

  useEffect(() => {
    window.addEventListener(
      'FMO_REDIRECT_MODAL_INTENT',
      handleFmoRedirectModalIntent
    );

    return () => {
      window.removeEventListener(
        'FMO_REDIRECT_MODAL_INTENT',
        handleFmoRedirectModalIntent
      );
    };
  }, [handleFmoRedirectModalIntent]);

  const handleMenuClick = useCallback(() => {
    setMobileNavigationVisible(!mobileNavigationVisible);
  }, [mobileNavigationVisible]);

  const openSubNav = useCallback(
    (items) => {
      setNavigationHistory([...navigationHistory, items]);
    },
    [navigationHistory]
  );

  const goBackSubNav = useCallback(() => {
    setNavigationHistory(navigationHistory.slice(0, -1));
  }, [navigationHistory]);

  const getSuggestions = useCallback(
    query => props.getSuggestions(query, props.autocompleteApi, props.saytApi),
    [props.getSuggestions, props.autocompleteApi, props.saytApi]
  );

  const handlePopupClose = (e) => {
    e.preventDefault();
    toggleMobileRedirectPopup(false);
    setTargetMobileSiteUrl(undefined);
  };

  const handleMobileRedirectPopupClick = () => {
    window.location.href = targetMobileSiteURl;
  };

  const handleHeaderClick = (e) => {
    if (e.target.tagName !== 'A') {
      return;
    }

    const clickedLink = e.target.href.toLowerCase();
    if (clickedLink.includes(props.mobileSiteBaseUrl)) {
      e.preventDefault();
      setTargetMobileSiteUrl(clickedLink);
      toggleMobileRedirectPopup(true);
    }
  };

  const positionTop =
    refHeaderWrapper.current !== null &&
    refHeaderWrapper.current.offsetTop + refHeaderWrapper.current.offsetHeight;

  useEffect(() => {
    if (props.useOnlyInitialNavigation) {
      return;
    }

    get(props.navigationApi, undefined, { credentials: 'omit' }).then(
      (nav) => {
        setNavigation(nav);
      },
      () => {
        // eslint-disable-next-line no-console
        console.warn(
          'Cannot load navigation from navigation API. Falling back to JSON on CND'
        );
        get(props.navigationFallbackUrl, undefined, {
          credentials: 'omit'
        }).then(
          (nav) => {
            setNavigation(nav);
          },
          () => {
            // eslint-disable-next-line no-console
            console.error('Cannot load navigation data from fallback.');
          }
        );
      }
    );
  }, [props.navigationApi, props.useOnlyInitialNavigation]);

  useEffect(() => {
    if (props.fmoRedirectPopupContentUrl) {
      get(props.fmoRedirectPopupContentUrl, undefined, {
        credentials: 'omit'
      }).then(
        (res) => {
          setFmoRedirectPopupContent(res.styleTags + res.html);
        },
        (e) => {
          console.error(
            "Cannot load FMO popup content. Popup won't be visible"
          );
          console.error(e);
        }
      );
    }
  }, [props.fmoRedirectPopupContentUrl]);

  useEffect(() => {
    if (!props.storeApi || !props.userCoordinates) {
      return;
    }
    fetch(`${props.storeApi}?maxCount=1&nearBy=${props.userCoordinates}`, {})
      .then(res => res.json())
      .then(
        (result) => {
          if (!result.hasOwnProperty('payload')) {
            console.error(
              'Store API returned unexpected response, expected payload property.'
            );
            return;
          }
          // first store is the closest one
          const closestStoreNumber = result.payload.stores[0].code;
          localStorage.setItem('closestStoreNumber', closestStoreNumber);
        },
        (error) => {
          console.log(`Error retrieving data from Store API: ${error}`);
        }
      );
  }, [props.storeApi, props.userCoordinates]);

  return (
    <div
      onClick={props.mobileSiteBaseUrl ? handleHeaderClick : undefined}
      aria-hidden="true"
    >
      {fmoRedirectPopupContent && openMobileRedirectPopup && (
        <Popup title="Taking you to our Mobile page" onClose={handlePopupClose}>
          <PopupContentWrapper aria-hidden="true">
            {/* eslint-disable-next-line react/no-danger */}
            <div
              dangerouslySetInnerHTML={{ __html: fmoRedirectPopupContent }}
            />
            <PopupActionWrapper>
              <Button onClick={handleMobileRedirectPopupClick}>
                Visit Mobile page
              </Button>
              <Link href="#" onClick={handlePopupClose}>
                Go back
              </Link>
            </PopupActionWrapper>
          </PopupContentWrapper>
        </Popup>
      )}
      <Wrapper data-qa={props.qa} ref={refHeaderWrapper}>
        <HeaderLogoWrapper>
          <HeaderLogo
            desktopLogoSrc={props.logoVariables.desktopLogoSrc}
            desktopLogoWidth={props.logoVariables.desktopLogoWidth}
            desktopLogoHeight={props.logoVariables.desktopLogoHeight}
            handHeldLogoSrc={props.logoVariables.handHeldLogoSrc}
            handHeldLogoWidth={props.logoVariables.handHeldLogoWidth}
            handHeldLogoHeight={props.logoVariables.handHeldLogoWidth}
            logoAlt={props.logoVariables.logoAlt}
            logoLink={props.logoVariables.logoLink}
          />
        </HeaderLogoWrapper>
        <Slide ref={searchRef} visible={openSearch}>
          <HeaderSearchProduct
            visible={openSearch}
            placeholder={props.searchProductVariables.placeholder}
            btnTitle={props.searchProductVariables.btnTitle}
            searchAction={props.searchProductVariables.searchAction}
            theme={props.theme}
            getSuggestions={getSuggestions}
            onSubmit={props.onSubmit}
            onSuggestionSelected={props.onSuggestionSelected}
          />
        </Slide>
        <DesktopHeaderSearchProduct
          visible
          placeholder={props.searchProductVariables.placeholder}
          btnTitle={props.searchProductVariables.btnTitle}
          searchAction={props.searchProductVariables.searchAction}
          theme={props.theme}
          getSuggestions={getSuggestions}
          onSubmit={props.onSubmit}
          onSuggestionSelected={props.onSuggestionSelected}
        />
        <HeaderMenu>
          <TouchHeaderLinkItem
            icon="Menu"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/HamburgerWhiteBrightworld.svg"
            label={props.menuLabel}
            onClick={handleMenuClick}
          />
          <TouchHeaderLinkItem
            icon="Search"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/SearchWhiteBrightworld.svg"
            interaction="SearchHeaderLinkItem"
            label={props.searchLabel}
            onClick={() => toggleSearch(!openSearch)}
          />
          <HeaderMenuSpacer />
          <LinkItem
            icon="Stores"
            iconCDNDesktop="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/LocationPurpleBrightworld.svg"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/LocationWhiteBrightworld.svg"
            label={props.storesLabel}
            href={props.storesLink}
          />
          {props.userSignedIn && (
            <AccountMenu
              items={props.accountMenu}
              notificationsApi={props.notificationsApi}
              onNotificationsShown={props.onNotificationsShown}
            >
              {({ notificationsCount }) => (
                <LinkItem
                  icon="User"
                  iconCDNDesktop="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/UserSignedInPurpleBrightworld.svg"
                  iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/UserSignedInWhiteBrightworld.svg"
                  label={props.userLabelSignedIn}
                  href={props.userLinkSignedIn}
                  badge={{
                    count: notificationsCount,
                    color: props.theme.colors.error.default,
                    displayZero: false,
                    shortenMultipleDigitNumbers: true
                  }}
                />
              )}
            </AccountMenu>
          )}
          {!props.userSignedIn && (
            <LinkItem
              icon="UserOutline"
              iconCDNDesktop="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/UserPurpleBrightworld.svg"
              iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/UserWhiteBrightworld.svg"
              label={props.userLabelSignedOut}
              href={props.userLinkSignedOut}
            />
          )}
          <LinkItem
            icon="Basket"
            iconCDNDesktop="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/BasketPurpleBrightworld.svg"
            iconCDNMobile="https://currys-ssl.cdn.dixons.com/css/themes/dory/icons/BasketWhiteBrightworld.svg"
            label={props.basketLabel}
            href={props.basketLink}
            badge={{
              count: props.basketItemsCount,
              color: props.theme.colors.buy.default,
              displayZero: true
            }}
          />
        </HeaderMenu>
      </Wrapper>
      {mobileNavigationVisible && (
        <MobileHeaderNavigation
          navigationData={navigation}
          navigationHistory={navigationHistory}
          goBack={goBackSubNav}
          openSubNav={openSubNav}
          positionTop={positionTop}
        />
      )}
      <DesktopHeaderNavigation navigationData={navigation} />
    </div>
  );
};

Header.displayName = 'Header';

const accountMenuItemPropTypes = PropTypes.shape({
  link: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  notificationsLabel: PropTypes.string
}).isRequired;

Header.propTypes = {
  children: PropTypes.node,
  initialNavigation: PropTypes.shape({
    catalog: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    }),
    pages: PropTypes.shape({
      id: PropTypes.number,
      tenant: PropTypes.string,
      type: PropTypes.string,
      name: PropTypes.string,
      version: PropTypes.string,
      nav: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      )
    })
  }),
  useOnlyInitialNavigation: PropTypes.bool,
  logoVariables: PropTypes.shape({
    desktopLogoSrc: PropTypes.string,
    desktopLogoWidth: PropTypes.number,
    desktopLogoHeight: PropTypes.number,
    handHeldLogoSrc: PropTypes.string,
    handHeldLogoWidth: PropTypes.number,
    handHeldLogoHeight: PropTypes.number,
    logoAlt: PropTypes.string,
    logoLink: PropTypes.string
  }).isRequired,
  searchProductVariables: PropTypes.shape({
    placeholder: PropTypes.string,
    btnTitle: PropTypes.string,
    searchAction: PropTypes.string.isRequired
  }).isRequired,
  qa: PropTypes.string,
  navigationApi: PropTypes.string.isRequired,
  navigationFallbackUrl: PropTypes.string,
  autocompleteApi: PropTypes.string.isRequired,
  notificationsApi: PropTypes.string,
  saytApi: PropTypes.string.isRequired,
  /**
   * @param {string} query
   * @param {string} autocompleteApi
   * @param {string} saytApi
   * @return {object}
   */
  getSuggestions: PropTypes.func,
  theme: PropTypes.shape({}).isRequired,
  basketLabel: PropTypes.string,
  basketLink: PropTypes.string.isRequired,
  menuLabel: PropTypes.string,
  searchLabel: PropTypes.string,
  storesLabel: PropTypes.string,
  storesLink: PropTypes.string.isRequired,
  userLabelSignedOut: PropTypes.string,
  userLabelSignedIn: PropTypes.string,
  userLinkSignedIn: PropTypes.string.isRequired,
  userLinkSignedOut: PropTypes.string.isRequired,
  accountMenu: PropTypes.shape({
    orders: accountMenuItemPropTypes,
    savedForLater: accountMenuItemPropTypes,
    details: accountMenuItemPropTypes,
    stayInTouch: accountMenuItemPropTypes,
    findOutMore: accountMenuItemPropTypes,
    signOut: accountMenuItemPropTypes
  }).isRequired,
  basketItemsCount: PropTypes.number,
  userSignedIn: PropTypes.bool,
  onSubmit: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  onNotificationsShown: PropTypes.func,
  mobileSiteBaseUrl: PropTypes.string,
  /**
   * URL to content-renderer is expected. SSR is required.
   */
  fmoRedirectPopupContentUrl: PropTypes.string,
  userCoordinates: PropTypes.string,
  storeApi: PropTypes.string.isRequired
};

Header.defaultProps = {
  children: undefined,
  initialNavigation: undefined,
  useOnlyInitialNavigation: false,
  qa: undefined,
  getSuggestions: async (query, autocompleteApi, saytApi) => {
    const getData = url => fetch(url)
      .then(res => res.json())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Get suggestion fetch error:', error);
      });

    const getClosestStoreNumber = () => localStorage.getItem('closestStoreNumber');
    const storeNumber = parseInt(getClosestStoreNumber(), 10);

    const [terms = [], sayt = { Results: [] }] = await Promise.all([
      getData(`${autocompleteApi}?query=${query}&max_results=5&beginning=1`),
      getData(
        `${saytApi}?q=${query}&do=sayt${
          storeNumber ? `&store_number=${storeNumber}` : ''
        }`
      )
    ]);

    return [
      ...terms.map(title => ({ type: 'term', title })),
      ...sayt.Results.map(p => ({ type: 'sayt', ...p }))
    ];
  },
  basketLabel: 'Basket',
  menuLabel: 'Menu',
  navigationFallbackUrl:
    'https://currys-ssl.cdn.dixons.com/smartphoneApp/navigation/navigation.json',
  notificationsApi: undefined,
  searchLabel: 'Search',
  storesLabel: 'Stores',
  userLabelSignedOut: 'Sign in',
  userLabelSignedIn: 'Hello',
  basketItemsCount: 0,
  userSignedIn: false,
  onSubmit: () => {},
  onSuggestionSelected: () => {},
  onNotificationsShown: () => {},
  mobileSiteBaseUrl: 'currys.co.uk/mobile',
  fmoRedirectPopupContentUrl: undefined,
  userCoordinates: undefined
};

export default withTheme(Header);
